import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Icons, Text } from 'widget/components';
import { Position } from '../message-env';
import { useChatBodyContext } from 'widget/content/chat/chat-body/chat-body-context';
import {
  useCopyToClipboard,
  useGetMessagePreview,
  useIsPrimaryDark,
  useOnClickOutside,
} from 'widget/hooks';
import { useMsgContext } from '../msg-context';
import { useWidgetContext } from 'widget/widget-context';
import * as Env from 'widget/env';

import { findPosition } from './actions';

interface ContextMenu {
  children: React.ReactNode;
  isContextMenuActive: boolean;
  setIsContextMenuActive: Env.SetState<boolean>;
  triggerPosition: Position | undefined;
}

const ContextMenu = ({
  children,
  isContextMenuActive,
  setIsContextMenuActive,
  triggerPosition,
}: ContextMenu) => {
  const { widget } = useWidgetContext();
  const { setQuotedMessage } = useChatBodyContext();
  const { msg } = useMsgContext();

  const [position, setPosition] = useState<Position>();

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const copyToClipboard = useCopyToClipboard();
  const getMessagePreview = useGetMessagePreview();
  const isPrimaryDark = useIsPrimaryDark();
  const targetRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(targetRef, () => setIsContextMenuActive(false));

  useEffect(() => {
    if (!isContextMenuActive) return;

    const res = findPosition(targetRef, { containerRef, triggerPosition });
    setPosition(res);
  }, [isContextMenuActive]);

  const handleCopy = () => {
    copyToClipboard(msg.content);
    setIsContextMenuActive(false);
  };

  const handleReply = () => {
    setQuotedMessage({ id: msg.id, preview: getMessagePreview(msg) });
    setIsContextMenuActive(false);
  };

  const fill =
    widget.theme == 'dark' ? 'white' : !isPrimaryDark ? 'dark' : undefined;

  return (
    <div
      className={cn('whatcrm-widget-context-menu', {
        'whatcrm-widget-context-menu_active': isContextMenuActive,
      })}
      ref={containerRef}
    >
      <div
        className="whatcrm-widget-context-menu__wrapper"
        ref={targetRef}
        style={position}
      >
        {children}

        <div className="whatcrm-widget-context-menu__list">
          <button
            className="whatcrm-widget-context-menu__item"
            onClick={handleReply}
          >
            <Icons.ArrowBendUpLeft fill={fill} size={20} />
            <Text fontSize={12}>{t`Reply`}</Text>
          </button>

          {msg.content && (
            <button
              className="whatcrm-widget-context-menu__item"
              onClick={handleCopy}
            >
              <Icons.Files fill={fill} size={20} />
              <Text fontSize={12}>{t`Copy`}</Text>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextMenu;
